import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ArrowLeft, Loader2, StickyNote, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  plannerAccess,
  listPlannerEvents,
  createPlannerEvent,
  updatePlannerEvent,
} from "../lib/api";

const parseISO = (s) => {
  const [y, m, d] = (s || "").split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const fmtLong = (d) =>
  d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

export default function PlannerDayNotes() {
  const { date } = useParams();
  const navigate = useNavigate();
  const [access, setAccess] = useState({ has_access: false, loading: true });
  const [loading, setLoading] = useState(true);
  const [eventId, setEventId] = useState(null);
  const [text, setText] = useState("");
  const [savedText, setSavedText] = useState("");
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  const dateObj = useMemo(() => parseISO(date), [date]);

  useEffect(() => {
    plannerAccess()
      .then((res) => setAccess({ ...res, loading: false }))
      .catch(() => setAccess({ has_access: false, loading: false }));
  }, []);

  // Load the day_note for this date.
  useEffect(() => {
    if (!access.has_access) return;
    setLoading(true);
    listPlannerEvents(date, date)
      .then((list) => {
        const note = (list || []).find((e) => e.kind === "day_note" && !e.period_label);
        setEventId(note?.id || null);
        setText(note?.notes || "");
        setSavedText(note?.notes || "");
      })
      .catch(() => {
        setEventId(null);
        setText("");
        setSavedText("");
      })
      .finally(() => setLoading(false));
  }, [access.has_access, date]);

  // Debounced auto-save.
  useEffect(() => {
    if (loading || !access.has_access) return;
    if (text === savedText) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        if (eventId) {
          await updatePlannerEvent(eventId, { notes: text });
        } else {
          const ev = await createPlannerEvent({
            date,
            kind: "day_note",
            notes: text,
            activity_name: "Day notes",
            period_label: null,
          });
          setEventId(ev.id);
        }
        setSavedText(text);
      } catch {
        toast.error("Could not save notes.");
      } finally {
        setSaving(false);
      }
    }, 700);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [text, savedText, eventId, date, loading, access.has_access]);

  const goToDay = (delta) => navigate(`/planner/day/${isoDate(addDays(dateObj, delta))}`);

  if (access.loading) {
    return <p className="text-[#4a6984]/70 p-6" data-testid="day-notes-loading">Loading…</p>;
  }
  if (!access.has_access) {
    return (
      <div className="ae-card p-10 text-center max-w-xl mx-auto mt-8" data-testid="day-notes-upsell">
        <Lock className="h-9 w-9 mx-auto text-[#4a6984]/70 mb-3" />
        <h3 className="font-extrabold text-xl text-[#1c2d3d] mb-2">Planner is a Pro feature</h3>
        <Link to="/pricing">
          <Button className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold">Upgrade to Pro</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4" data-testid="day-notes-root">
      <div className="flex items-center justify-between gap-2">
        <Link
          to={`/dashboard?view=planner&date=${date}`}
          className="text-sm font-semibold text-[#4a6984] hover:text-[#1c2d3d] inline-flex items-center gap-1"
          data-testid="day-notes-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to month
        </Link>
        <div className="inline-flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => goToDay(-1)} data-testid="day-notes-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => goToDay(1)} data-testid="day-notes-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="ae-card p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-1">
          <StickyNote className="h-5 w-5 text-[#1c9e7e]" />
          <h2 className="font-extrabold text-2xl text-[#1c2d3d]" data-testid="day-notes-date">{fmtLong(dateObj)}</h2>
        </div>
        <p className="text-sm text-[#4a6984] mb-4">
          Private notes for this day. These stay here — they don&apos;t show on the month calendar.
        </p>
        {loading ? (
          <div className="flex items-center gap-2 text-[#4a6984] py-8">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading notes…
          </div>
        ) : (
          <>
            <Textarea
              rows={16}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"e.g.\n• Follow up with Maria's parent about reading\n• Prep science materials for tomorrow\n• Remember: fire drill at 10:15"}
              className="text-sm leading-relaxed"
              data-testid="day-notes-textarea"
            />
            <p className="text-[11px] text-[#4a6984]/60 mt-2 h-4" data-testid="day-notes-status">
              {saving ? "Saving…" : text === savedText ? "All changes saved." : ""}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
